// Document header schema
// Stores authorship, grid settings, and display options

import { z } from 'zod';

export const DocumentHeaderSchema = z.object({
  authorship: z.object({
    created_by: z.string().optional(),
    modified_by: z.string().optional(),
    creation_date: z.string().optional(),
    modification_date: z.string().optional(),
    file_version_major: z.number().int().optional(),
    file_version_minor: z.number().int().optional(),
    app_version_major: z.number().int().optional(),
    app_version_minor: z.number().int().optional(),
    company: z.string().optional(),
    project: z.string().optional(),
    logo: z.string().optional(),
  }).optional(),
  grid_settings: z.object({
    grid_enabled: z.boolean().optional(),
    snap_to_grid: z.boolean().optional(),
    grid_type: z.enum(['orthogonal', 'isometric']).optional(),
    grid_granularity: z.enum(['fine', 'standard', 'coarse']).optional(),
    presentation_mode: z.boolean().optional(),
  }).optional(),
  display_options: z.record(z.string(), z.unknown()).optional(),
  /**
   * The isometric lattice this document is drawn on, in pixels.
   *
   * Desktop's iso lattice is `x = cos30·q·m`, `y = ½·q·n` with `m+n` even, where
   * `q` is `grid_granularity` ({fine, standard, coarse} → {.25, 1, 2},
   * `flow_document.cpp:574`). `q` is in Desktop GRID UNITS, so it says nothing
   * about pixels on its own — the screen lattice is `g = q · unit_px`.
   *
   * `unit_px` = **how many pixels one grid unit is**. A document-level fact with
   * two sources, which is why it is not called `import_transform` (an earlier
   * draft did, from an import-only view of the world):
   *  - **Imported** → derived, `150 / median connected distance`, measured on the
   *    RAW coordinates at import. Nothing downstream can reconstruct it from the
   *    scaled geometry alone, so it must be recorded there.
   *  - **Authored in Connect** → chosen. There is no Desktop original to match,
   *    so a new isometric model simply picks one.
   *
   * `parity` is the required value of `(m + n) mod 2`. Import mirrors Y about
   * `maxRawY`, substituting `n' = n_max − n`, so the post-import rule becomes
   * `(m + n') ≡ n_max (mod 2)`: when `n_max` is ODD the parity rule INVERTS, and
   * 3 of the 9 corpus iso fixtures are in that case. Captured at import because
   * `n_max` is an exact integer there and a float-derived guess afterwards. A
   * natively-authored model has no mirror, so its parity is 0.
   *
   * Absent ⇒ do not snap. Written only for isometric documents.
   */
  lattice: z.object({
    unit_px: z.number(),
    parity: z.union([z.literal(0), z.literal(1)]),
  }).optional(),
});

export type DocumentHeader = z.infer<typeof DocumentHeaderSchema>;

/**
 * Pixels per grid unit for an isometric model authored in Connect.
 *
 * An imported model derives this from its Desktop original (150 / median
 * connected distance). A native model has no original, so it picks: 20px per
 * unit, at the STANDARD quantum (q=1), gives `g = 20px` — the same cell size as
 * the orthogonal grid the rest of Connect draws, so an isometric canvas feels
 * like the same application rather than a different one.
 */
export const NATIVE_LATTICE_UNIT_PX = 20;

/**
 * The `document_header` for a model created in Connect, in the given basis.
 *
 * ⚠️ Basis is a CREATION-TIME decision, not an edit-time one. Changing a
 * populated model's basis is a stored coordinate transform in Desktop
 * (`apply_iso_perspective` rewrites every location and polyline point, paired
 * with `undo_iso_perspective` on the way back) — and `document_header` is outside
 * Connect's undo stack, so a mid-life switch would be un-undoable. Choosing at
 * birth has none of those problems: there is no geometry to transform.
 *
 * ⚠️ An isometric model MUST get a `lattice` here. Without one `isoLatticeFor`
 * returns null, nothing snaps, and the canvas silently draws a square grid over
 * an isometric document — worse than not offering the choice, because it looks
 * like it worked.
 */
export function newDocumentHeader(
  project: string,
  basis: 'orthogonal' | 'isometric',
  creationDate: string,
): DocumentHeader {
  const authorship = { project, creation_date: creationDate };
  if (basis === 'orthogonal') return { authorship };
  return {
    authorship,
    grid_settings: {
      grid_enabled: true,
      snap_to_grid: true,
      grid_type: 'isometric',
      // STANDARD (q=1) with unit_px=20 ⇒ g = 20px. `fine` (q=0.25) would give a
      // 5px cell: below the renderer's 6px legibility floor, and a snap that
      // fine is indistinguishable from no snap at all.
      grid_granularity: 'standard',
    },
    // No Desktop original ⇒ no Y-mirror ⇒ no parity inversion. Parity 0 is the
    // plain `m+n even` rule.
    lattice: { unit_px: NATIVE_LATTICE_UNIT_PX, parity: 0 },
  };
}
